import type { CuratorRosterRow } from '@/api/curatorGroups';

/**
 * Выбор учеников пачкой на экране куратора (D40, договор
 * `docs/CONTRACTS/curator-groups.md` §4 и §5).
 *
 * ⚠️ СЕРВЕР ПРОВЕРЯЕТ ПАЧКУ ЦЕЛИКОМ: отказ по любой записи — и не пишется
 * ничего. Поэтому в удаление складываем только записи ЭТОЙ группы: одна чужая
 * строка уронит весь запрос 404-м `student_not_found`, а куратор увидит, что
 * «ничего не произошло», без объяснения.
 *
 * ⚠️ ВЫБОР ПЕРЕЖИВАЕТ ПЕРЕЧИТЫВАНИЕ СПИСКА. После удачной пачки список читается
 * заново; гасить выбор целиком — значит терять работу куратора, который отметил
 * полсотни человек. Но записи, исчезнувшие из курса (их забрали секундой
 * раньше), держать нельзя: они уйдут в следующий запрос и уронят его.
 */

/** Ровно 120 символов — предел сервера (`invalid_name`). */
const NAME_LIMIT = 120;

export function toggleSelected(selected: ReadonlySet<string>, id: string): Set<string> {
  const next = new Set(selected);
  if (!next.delete(id)) next.add(id);
  return next;
}

/**
 * «Выбрать всех найденных» — по ОТФИЛЬТРОВАННОМУ списку, а не по тому, что
 * влезло в окно отрисовки: иначе кнопка молча выбирала бы первые 50 из 554.
 */
export function selectAllFiltered(
  filtered: readonly CuratorRosterRow[],
  selected: ReadonlySet<string> = new Set(),
): Set<string> {
  const next = new Set(selected);
  for (const r of filtered) next.add(r.enrollment_id);
  return next;
}

/** Оставить в выборе только то, что ещё есть в списке курса. */
export function keepExisting(selected: ReadonlySet<string>, rows: readonly CuratorRosterRow[]): string[] {
  const alive = new Set(rows.map((r) => r.enrollment_id));
  return [...selected].filter((id) => alive.has(id));
}

/**
 * Раскладка выбранных по своим группам для удаления.
 *
 * `skipped` — сколько выбранных пропущено (без группы или в чужой группе). Их
 * число показывается словами: молчаливое «часть не убралась» выглядит как сбой.
 */
export function removableByGroup(
  selected: ReadonlySet<string>,
  rows: readonly CuratorRosterRow[],
  myGroupIds: ReadonlySet<string>,
): { byGroup: Map<string, string[]>; skipped: number } {
  const byGroup = new Map<string, string[]>();
  let skipped = 0;
  for (const r of rows) {
    if (!selected.has(r.enrollment_id)) continue;
    if (!r.group_id || !myGroupIds.has(r.group_id)) {
      skipped += 1;
      continue;
    }
    const list = byGroup.get(r.group_id) ?? [];
    list.push(r.enrollment_id);
    byGroup.set(r.group_id, list);
  }
  return { byGroup, skipped };
}

export function validGroupName(name: string): boolean {
  const trimmed = name.trim();
  return trimmed.length > 0 && trimmed.length <= NAME_LIMIT;
}
